import { ACCENT_GOLD, GOLD } from '../lib/medievalTheme';
import { t } from '../localization/t';

/**
 * Flip-over affordance for two-sided cards (Druid avatar, etc.).
 *
 * Visual-only: doesn't own face state. Callers track which face is
 * showing and pass `face` + `onFlip`. The parent decides whether the
 * flip is local to one tile (inspector preview) or propagates further
 * (e.g. the gameboard avatar mesh).
 *
 * Two placement modes:
 *   - `position="overlay"` (default). Absolutely positioned in the
 *     bottom-left corner of the nearest positioned ancestor. Use this
 *     on a card image to anchor the flip control to the card itself.
 *   - `position="inline"`. No absolute positioning. Use this when
 *     placing the button in a row beside other controls (e.g. the
 *     deck builder's quantity-indicator row or the inspector's name
 *     row). The parent is responsible for placement.
 *
 * Sized in cqw so the button rides the canvas-uniform scaling like
 * everything else, but the glyph stroke / 1px border stay in px so
 * they render crisp at every scale.
 */
export default function CardFlipButton({
  face = 'front',
  onFlip,
  size = '1.8cqw',
  title,
  position = 'overlay',
  className = '',
}) {
  const isBack = face === 'back';
  const label = title || (isBack ? t('Show front face') : t('Show back face'));
  const isOverlay = position === 'overlay';

  const positionStyle = isOverlay
    ? { position: 'absolute', bottom: '0.5cqw', left: '0.5cqw', zIndex: 5 }
    : null;

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onFlip?.();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-95 cursor-pointer ${className}`}
      style={{
        ...positionStyle,
        width: size,
        height: size,
        borderRadius: '50%',
        background: isBack
          ? `radial-gradient(circle at 35% 30%, ${ACCENT_GOLD}, #7a5414 85%)`
          : 'rgba(20, 14, 8, 0.78)',
        border: `1px solid ${isBack ? ACCENT_GOLD : `${GOLD} 0.55)`}`,
        color: isBack ? '#1a1208' : '#fbe9b8',
        boxShadow: isBack
          ? `0 0 0 1px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.55), 0 0 14px ${GOLD} 0.6)`
          : '0 0 0 1px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: 0,
      }}
    >
      <FlipGlyph />
    </button>
  );
}

// "Two-sided card" glyph: two offset rounded rectangles, the front one
// subtly filled. Reads clearly at button sizes (1.4cqw–2cqw) where
// curved-arrow glyphs become unreadable fiddle marks. The button's
// background color already carries the front/back state, so the glyph
// stays constant. Its job is just to say "there's another side".
function FlipGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{ width: '72%', height: '72%' }}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="12" height="16" rx="1.8" />
      <rect x="9" y="5" width="12" height="16" rx="1.8" fill="currentColor" fillOpacity="0.32" />
    </svg>
  );
}
